import cn from 'classnames'
import {FC, useEffect, useRef, useState} from 'react'

import {TrustpilotWidgetVariant, useTrustpilotContent} from '@/content/trustpilot'
import {theme} from '@/theme/theme'

import {Spinner} from '../Spinner'
import {TrustpilotStarsSkeleton} from './TrustpilotStarsSkeleton'

declare global {
  interface Window {
    Trustpilot: any
  }
}

// The TrustBox iframe fetches its reviews after `load`, so the swap waits a moment before fading.
const REVEAL_DELAY = 150
const REVEAL_DURATION = 300

type TrustpilotWidgetProps = {
  variant: TrustpilotWidgetVariant
}

export const TrustpilotWidget: FC<React.PropsWithChildren<TrustpilotWidgetProps>> = ({variant}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isRevealed, setIsRevealed] = useState(false)
  const {trustpilotLinkHref, trustpilotSettings} = useTrustpilotContent(variant)

  useEffect(() => {
    const element = ref.current
    if (!element) {
      return
    }

    // Trustpilot's script injects the iframe; `load` doesn't bubble, so listen in the capture phase.
    const onLoad = () => setIsLoaded(true)
    element.addEventListener('load', onLoad, true)

    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(element, true)
    }

    return () => element.removeEventListener('load', onLoad, true)
  }, [])

  return (
    <>
      <div className={cn('wrapper', isLoaded && 'loaded')}>
        <div
          ref={ref}
          className="trustpilot-widget"
          data-locale="en-GB"
          data-template-id={trustpilotSettings.templateId}
          data-businessunit-id={trustpilotSettings.businessId}
          data-style-height={trustpilotSettings.height}
          data-style-width={trustpilotSettings.width}
          data-theme={trustpilotSettings.theme}
          // data-stars -> Our latest reviews, data-tags -> Our favourite reviews
          // data-stars={trustpilotSettings.stars}
          data-tags={trustpilotSettings.tags}
        />
        {!isRevealed && (
          <a
            className="placeholder"
            href={trustpilotLinkHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Trustpilot"
            // Unmount once faded out, as `visibility: hidden` keeps the skeleton animating.
            onTransitionEnd={(event) => {
              if (event.target === event.currentTarget) {
                setIsRevealed(true)
              }
            }}
          >
            {variant === TrustpilotWidgetVariant.stars ? (
              <TrustpilotStarsSkeleton />
            ) : (
              <Spinner color={theme.palette.text.default} />
            )}
          </a>
        )}
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 100%;
          min-height: ${trustpilotSettings.height};
        }
        .trustpilot-widget {
          opacity: 0;
          transition: opacity ${REVEAL_DURATION}ms ease ${REVEAL_DELAY}ms;
        }
        .placeholder {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: ${variant === TrustpilotWidgetVariant.stars ? 'center' : 'flex-start'};
          justify-content: center;
          transition: opacity ${REVEAL_DURATION}ms ease ${REVEAL_DELAY}ms,
            visibility 0s linear ${REVEAL_DELAY + REVEAL_DURATION}ms;
        }
        .loaded .trustpilot-widget {
          opacity: 1;
        }
        .loaded .placeholder {
          opacity: 0;
          visibility: hidden;
        }
      `}</style>
    </>
  )
}
